// src/registered/site-admin/meter_catalogue/meterCatalogueInteractionService.js
//
// 🛰️ METER DATA CATALOGUE — interaction service
// ----------------------------------------------------------------------------
// CRUD : SUSTINA DB gateway (dbPayload + GoldenSourceHelper), proxied by the BFF
//        straight through to nrgCore -> ESG.usp_MeterConcept_* /
//        ESG.usp_MeterRegistration_* / ESG.usp_MeterReading_* in RDB2.
//        See .claude/db/create_meter_catalogue.sql.
//
// Same shape as knowledgeBaseInteractionService.js.

import { GoldenSourceHelper } from "../../../app/Switch_Core_React_Standards";
import { dbPayload } from "../../../auth/registered/SEC_00_DB_Payload";

const ROOT = "/api/v1/sustina/meter-catalogue";

const post = async (path, body, config, intent = "") => {
  const raw = await dbPayload(
    `${ROOT}${path}`,
    "POST",
    config.siteKey,
    GoldenSourceHelper.prepareForApi(body || {}),
    intent,
  );
  return GoldenSourceHelper.normalize(raw);
};

const ok = (res) => (res && res.success ? res.data ?? null : null);
const list = (res) => (res && res.success ? res.data || [] : []);

// ---------------------------------------------------------------------------
// Ref pick-lists
// ---------------------------------------------------------------------------

export const fetchRefLists = async (config) => {
  const [commodities, schemes, statuses, methods, units] = await Promise.all([
    post("/ref/commodity/list", {}, config).then(list).catch(() => []),
    post("/ref/identifier-scheme/list", {}, config).then(list).catch(() => []),
    post("/ref/concept-status/list", {}, config).then(list).catch(() => []),
    post("/ref/aggregation-method/list", {}, config).then(list).catch(() => []),
    post("/ref/unit/list", {}, config).then(list).catch(() => []),
  ]);
  return { commodities, schemes, statuses, methods, units };
};

// ---------------------------------------------------------------------------
// Concepts
// ---------------------------------------------------------------------------

export const fetchConcepts = async (filters = {}, config) => {
  const res = await post(
    "/concept/list",
    {
      CommodityCode: filters.commodityCode || null,
      StatusCode: filters.statusCode || null,
      Search: filters.search || null,
      IncludeInactive: filters.includeInactive ? 1 : 0,
    },
    config,
  );
  return list(res);
};

export const getConcept = async (meterConceptId, config) => {
  const res = await post("/concept/get", { MeterConceptId: meterConceptId }, config);
  if (!res.success) return null;
  const d = res.data || {};
  // proc returns concept + identifiers + attributeMaps + aliases as separate sets
  return {
    concept: d.concept || d.Concept || d,
    identifiers: d.identifiers || d.Identifiers || [],
    attributeMaps: d.attributeMaps || d.AttributeMaps || [],
    aliases: d.aliases || d.Aliases || [],
  };
};

export const addConcept = async (payload, config) => {
  const res = await post("/concept/add", payload, config);
  if (!res.success) throw new Error(res.message || "Could not create the concept.");
  return res.data;
};

export const updateConcept = async (payload, config) => {
  const res = await post("/concept/update", payload, config);
  if (!res.success) throw new Error(res.message || "Could not update the concept.");
  return res.data;
};

export const removeConcept = async (meterConceptId, config) => {
  const res = await post("/concept/remove", { MeterConceptId: meterConceptId }, config);
  if (!res.success) throw new Error(res.message || "Could not retire the concept.");
  return true;
};

// ---------------------------------------------------------------------------
// Identifier mappings
// ---------------------------------------------------------------------------

export const addIdentifier = async (payload, config) => {
  const res = await post("/concept/identifier/add", payload, config);
  if (!res.success) throw new Error(res.message || "Could not add the identifier.");
  return res.data;
};

export const updateIdentifier = async (payload, config) => {
  const res = await post("/concept/identifier/update", payload, config);
  if (!res.success) throw new Error(res.message || "Could not update the identifier.");
  return res.data;
};

export const removeIdentifier = async (identifierId, config) => {
  const res = await post("/concept/identifier/remove", { IdentifierId: identifierId }, config);
  if (!res.success) throw new Error(res.message || "Could not remove the identifier.");
  return true;
};

// ---------------------------------------------------------------------------
// Concept -> ESG AttributeDefinition
// ---------------------------------------------------------------------------

export const setAttributeMap = async (payload, config) => {
  const res = await post("/concept/attribute-map/set", payload, config);
  if (!res.success) throw new Error(res.message || "Could not link the attribute.");
  return res.data;
};

export const removeAttributeMap = async (mapId, config) => {
  const res = await post("/concept/attribute-map/remove", { MapId: mapId }, config);
  if (!res.success) throw new Error(res.message || "Could not unlink the attribute.");
  return true;
};

// ---------------------------------------------------------------------------
// Deterministic resolve (scheme + identifier -> concept)
// ---------------------------------------------------------------------------

export const resolveIdentifier = async ({ scheme, identifier, context }, config) => {
  const res = await post(
    "/resolve",
    { SchemeCode: scheme, Identifier: identifier, MappingContext: context || null },
    config,
  );
  return ok(res);
};

// ---------------------------------------------------------------------------
// Meter registrations
// ---------------------------------------------------------------------------

export const fetchRegistrations = async (filters = {}, config) => {
  const res = await post(
    "/registration/list",
    {
      CorporateEntityId: filters.corporateEntityId || null,
      CommodityCode: filters.commodityCode || null,
      IncludeInactive: filters.includeInactive ? 1 : 0,
    },
    config,
  );
  return list(res);
};

export const addRegistration = async (payload, config) => {
  const res = await post("/registration/add", payload, config);
  if (!res.success) throw new Error(res.message || "Could not register the meter.");
  return res.data;
};

export const updateRegistration = async (payload, config) => {
  const res = await post("/registration/update", payload, config);
  if (!res.success) throw new Error(res.message || "Could not update the meter.");
  return res.data;
};

export const removeRegistration = async (meterRegistrationId, config) => {
  const res = await post("/registration/remove", { MeterRegistrationId: meterRegistrationId }, config);
  if (!res.success) throw new Error(res.message || "Could not remove the meter.");
  return true;
};

// ---------------------------------------------------------------------------
// Reading ingestion (source-agnostic JSON batch) + period roll-up
// ---------------------------------------------------------------------------

/**
 * @param {object[]} readings [{ meterSerial, scheme, identifier, tsUtc, value, unit, kind, context?, sourceRef? }]
 */
export const ingestReadings = async ({ sourceSystem, sourceKind, sourceReference, readings }, config) => {
  const res = await post(
    "/reading/ingest",
    {
      SourceSystem: sourceSystem,
      SourceKind: sourceKind || "MANUAL",
      SourceReference: sourceReference || null,
      ReadingsJson: JSON.stringify(readings || []),
    },
    config,
    "MeterReadingIngest",
  );
  if (!res.success) throw new Error(res.message || "Ingestion failed.");
  return res.data;
};

export const rollupToPeriod = async ({ corporateEntityId, periodStartUtc, periodEndUtc, meterConceptId }, config) => {
  const res = await post(
    "/reading/rollup",
    {
      CorporateEntityId: corporateEntityId,
      PeriodStartUtc: periodStartUtc,
      PeriodEndUtc: periodEndUtc,
      MeterConceptId: meterConceptId || null,
    },
    config,
  );
  return list(res);
};
